/**
 * The Add instruction redesign's pure pieces (26/10/2026): which enquiries can
 * become the instruction, an enquiry's quoted lines, an instruction's stored
 * lines back into the calculator, and the sentence in an email that asks for
 * the work. No DOM, no Firestore — tested in test/instruction-model.test.mjs.
 */
const OPEN_ENQ = ['Enquiry', 'Quoted', 'Needs Review', 'On Hold'];
const tail = (r) => String(r || '').split('/').pop() || '';

/**
 * Enquiries that could become this instruction: this client's, still open, or
 * won and not yet a project. Newest first.
 */
export function candidateEnquiries(enquiries = [], project = {}) {
  const cid = tail(project.clientRef);
  const name = String(project.clientName || '').trim().toLowerCase();
  return enquiries.filter((e) => {
    if (!e || (e.isTest && !project.isTest)) return false;
    const mine = (cid && tail(e.clientRef) === cid) || (!cid && name && String(e.clientName || '').trim().toLowerCase() === name);
    if (!mine) return false;
    if (OPEN_ENQ.includes(e.status)) return true;
    return e.status === 'Won' && !e.projectNo;
  }).sort((a, b) => String(b.receivedAt || b.createdAt || '').localeCompare(String(a.receivedAt || a.createdAt || '')));
}

/** An enquiry's quoted lines, marked as quoted (V1 lines: per-unit hours = row ÷ No). */
export function quotedLines(e) {
  if (e && e.feeCalc && Array.isArray(e.feeCalc.lines) && e.feeCalc.lines.length) {
    const ro = new Set((e.feeCalc.sections || []).filter((s) => s && s.rateOnly).map((s) => s.label));
    return e.feeCalc.lines.filter((l) => !(l.instruction && ro.has(l.instruction))).map((l) => ({ ...l, asQuoted: true }));
  }
  return (Array.isArray(e && e.calculatorItems) ? e.calculatorItems : []).filter((l) => l && l.item)
    .map((l) => ({ item: l.item, kind: 'work', stdHours: (Number(l.hours) || 0) / (Number(l.qty) || 1), qty: Number(l.qty) || 1, complexity: 1, source: 'V1', asQuoted: true }));
}
export const quotedRevisions = (e) => (e && e.feeCalc && e.feeCalc.revisions) ? { count: e.feeCalc.revisions.count, percent: e.feeCalc.revisions.percent, charged: !!e.feeCalc.revisions.charged } : { count: 0, percent: 20, charged: false };

/** An instruction's stored lines → calculator lines. The old project panel saved {item, qty, hours PER UNIT}. */
export function instructionLines(instr) {
  return (Array.isArray(instr && instr.calculatorItems) ? instr.calculatorItems : []).filter((l) => l && l.item).map((l) => (l.kind
    ? { ...l }
    : { item: l.item, kind: 'work', stdHours: Number(l.hours) || 0, qty: Number(l.qty) || 1, complexity: 1, source: 'Track' }));
}

/** The sentence that asks for the work — a simple heuristic: the first with a request verb. */
const ASK_RE = /\b(could you|can you|would you|please|we need|we'd like|we would like|i'd like|i would like|are you able|will you|kindly)\b/i;
export function requestSentence(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  const parts = t.match(/[^.!?]+[.!?]*/g) || [t];
  const hit = parts.findIndex((s) => ASK_RE.test(s));
  if (hit < 0) return null;
  // The ask often runs on into the next sentence ("…the rear? The builder says it needs a beam.").
  return (parts[hit] + (parts[hit + 1] && parts[hit + 1].length < 90 ? parts[hit + 1] : '')).trim();
}
